import { ChevronLeft, ChevronRight, PartyPopper } from 'lucide-react';
import {
  addMonths,
  firstOfMonth,
  fullDayLabel,
  isoDate,
  isWeekday,
  monthGridDays,
  monthLabel,
  todayUtc,
} from './dateUtils.js';

const WEEKDAY_HEADS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/**
 * Grille mensuelle mobile partagée (Mon planning / Mes imputations).
 * Navigation ← mois → + « Ce mois-ci », 7 colonnes lundi → dimanche,
 * week-end atténué, aujourd'hui et jours fériés marqués. Le contenu d'une
 * cellule est fourni par l'écran (`renderCell(date, iso)`), le détail du jour
 * sélectionné est rendu par l'écran sous la grille.
 */
export default function MobileMonthGrid({
  month,
  onMonthChange,
  selectedIso,
  onSelect,
  renderCell,
  cellLabel,
  holidayByIso,
  loading = false,
  subtitle = null,
  legend = null,
}) {
  const days = monthGridDays(month);
  const todayIso = isoDate(todayUtc());
  const curMonth = month.getUTCMonth();
  const isCurrentMonth = firstOfMonth(todayUtc()).getTime() === month.getTime();

  return (
    <div>
      <div className="m-weeknav">
        <button
          type="button"
          className="m-navbtn"
          aria-label="Mois précédent"
          title="Mois précédent"
          onClick={() => onMonthChange(addMonths(month, -1))}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="m-weeknav__title">
          {monthLabel(month)}
          {subtitle ? (
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
              {subtitle}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          className="m-navbtn"
          aria-label="Mois suivant"
          title="Mois suivant"
          onClick={() => onMonthChange(addMonths(month, 1))}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <button
          type="button"
          className="m-todaybtn"
          disabled={isCurrentMonth}
          onClick={() => onMonthChange(todayUtc())}
        >
          Ce mois-ci
        </button>
      </div>

      <div className="m-cal" data-loading={loading}>
        <div className="m-cal__heads" aria-hidden="true">
          {WEEKDAY_HEADS.map((l, i) => (
            <span key={l} className="m-cal__headcell" data-weekend={i >= 5}>
              {l}
            </span>
          ))}
        </div>
        <div className="m-cal__grid">
          {days.map((d) => {
            const iso = isoDate(d);
            const holiday = holidayByIso?.get(iso);
            const extra = cellLabel ? cellLabel(d, iso) : '';
            return (
              <button
                type="button"
                key={iso}
                className="m-cal__cell"
                data-outside={d.getUTCMonth() !== curMonth}
                data-weekend={!isWeekday(d)}
                data-today={iso === todayIso}
                data-selected={iso === selectedIso}
                data-holiday={Boolean(holiday)}
                aria-pressed={iso === selectedIso}
                aria-label={[
                  fullDayLabel(d),
                  iso === todayIso ? 'aujourd’hui' : '',
                  holiday ? `férié : ${holiday.name}` : '',
                  extra,
                ]
                  .filter(Boolean)
                  .join(', ')}
                onClick={() => onSelect(iso)}
              >
                <span className="m-cal__top">
                  <span className="m-cal__num">{d.getUTCDate()}</span>
                  {holiday ? (
                    <PartyPopper size={11} className="m-cal__holiday" aria-hidden="true" />
                  ) : null}
                </span>
                {loading ? null : renderCell(d, iso)}
              </button>
            );
          })}
        </div>
      </div>
      {legend}
    </div>
  );
}
